import {
    ButtonGhost,
    ButtonGradientPrimary,
    Callout,
    Colors,
    Icon,
    Text,
    type IconName,
} from '@amelio/ui-library';
import { useTranslation } from 'react-i18next';

import type { AiPromptChoiceOption } from '../AiPromptModal';
import type { TranslationKey } from '@/i18n-key';

import { ChoiceChips } from '@/components/ChoiceChips/ChoiceChips';

interface AiPromptChoiceStepProps {
    /** ui-library icon name shown next to the question header. */
    iconName: IconName;
    /** i18n key for the question header. */
    questionKey: TranslationKey;
    options: AiPromptChoiceOption[];
    /** The picked option's value: a choice step always has one. */
    value: string;
    onChange: (value: string) => void;
    onNext: () => void;
    onBack?: () => void;
    /** The last question step shows the generate button (with its own label) instead of "Next". */
    isFinal: boolean;
    generateButtonKey?: TranslationKey;
    isGenerating?: boolean;
    /** The server's refusal of this answer: shown under the options, and the step cannot move on until it changes. */
    error?: { message: string; items: string[] };
    /**
     * A refusal no answer here can lift (what it names is edited outside the modal): shown above the buttons, and
     * the step cannot move on while it stands.
     */
    blocked?: { message: string; items: string[] };
    /** 1-based step number, used only for stable data-testids. */
    stepNumber: number;
    dataTestId: string;
}

/** A single-choice question step: the same frame as the free-text step, with one chip per option. */
export const AiPromptChoiceStep = ({
    iconName,
    questionKey,
    options,
    value,
    onChange,
    onNext,
    onBack,
    isFinal,
    generateButtonKey,
    isGenerating = false,
    error,
    blocked,
    stepNumber,
    dataTestId,
}: AiPromptChoiceStepProps) => {
    const { t } = useTranslation();
    const stepTestId = `${dataTestId}-choice-step${stepNumber}`;

    return (
        <div className={'animate-fade-in-up flex flex-col gap-5'}>
            <div className={'mb-3 flex items-center gap-2'}>
                <div className={'h-10 w-10'}>
                    <Icon
                        name={iconName}
                        color={Colors.BASE_600}
                        size={36}
                    />
                </div>
                <Text
                    weight={'semibold'}
                    className={'text-lg text-slate-800 md:text-xl'}
                >
                    {t(questionKey)}
                </Text>
            </div>

            <div data-testid={stepTestId}>
                <ChoiceChips
                    options={options.map((option) => ({
                        value: option.value,
                        label: t(option.labelKey),
                    }))}
                    selected={[value]}
                    // A choice step is never blank: pressing the picked chip keeps it picked.
                    onToggle={onChange}
                    ariaLabel={t(questionKey)}
                    shape={'pill'}
                    disabled={isGenerating}
                    dataTestId={`${stepTestId}-option`}
                />
            </div>

            {error && (
                <div
                    role={'alert'}
                    className={'flex flex-col gap-1'}
                    data-testid={`${dataTestId}-error-step${stepNumber}`}
                >
                    <Text
                        size={'small'}
                        color={Colors.ERROR_800}
                    >
                        {error.message}
                    </Text>
                    {error.items.length > 0 && (
                        <ul className={'flex list-disc flex-col gap-1 pl-5'}>
                            {error.items.map((item, index) => (
                                <li key={`${index}-${item}`}>
                                    <Text
                                        size={'small'}
                                        color={Colors.ERROR_800}
                                    >
                                        {item}
                                    </Text>
                                </li>
                            ))}
                        </ul>
                    )}
                </div>
            )}

            {blocked && (
                <Callout
                    tone={'danger'}
                    role={'alert'}
                    dataTestId={`${dataTestId}-blocked-step${stepNumber}`}
                >
                    <p>{blocked.message}</p>
                    {blocked.items.length > 0 && (
                        <ul className={'mt-1 flex list-disc flex-col gap-1 pl-5'}>
                            {blocked.items.map((item, index) => (
                                <li key={`${index}-${item}`}>{item}</li>
                            ))}
                        </ul>
                    )}
                </Callout>
            )}

            <div
                className={`mt-6 flex items-center gap-3 ${onBack ? 'justify-between' : 'justify-end'}`}
            >
                {onBack && (
                    <ButtonGhost
                        size={'md'}
                        onClick={onBack}
                        leftIcon={
                            <Icon
                                name={'arrow-arrow-left'}
                                size={'sm'}
                                color={Colors.BASE_400}
                            />
                        }
                        dataTestId={`${dataTestId}-back-link`}
                    >
                        {t('common.back')}
                    </ButtonGhost>
                )}

                <ButtonGradientPrimary
                    size={'md'}
                    onClick={onNext}
                    disabled={!value || isGenerating || Boolean(error) || Boolean(blocked)}
                    dataTestId={
                        isFinal ? `${dataTestId}-generate-button` : `${dataTestId}-next-button`
                    }
                    // The button merges its className last (tailwind-merge), so these override its own width.
                    className={isFinal ? undefined : 'w-fit min-w-auto px-7'}
                    leftIcon={
                        isFinal ? (
                            <Icon
                                name={'essential-add-circle'}
                                size={'md'}
                                color={Colors.WHITE}
                            />
                        ) : undefined
                    }
                >
                    {isFinal && generateButtonKey ? t(generateButtonKey) : t('common.next')}
                </ButtonGradientPrimary>
            </div>
        </div>
    );
};
